import React, { useState } from "react";
import useReveal from "@/hooks/useReveal";

// Simple lat/lon → SVG projection helpers (equirectangular, 1000×500 viewBox)
const project = (lat, lon) => ({
  x: ((lon + 180) / 360) * 1000,
  y: ((90 - lat) / 180) * 500,
});

const COUNTRIES = [
  { name: "United Kingdom", city: "London", lat: 51.5, lon: -0.1, hub: true },
  { name: "Germany", city: "Frankfurt", lat: 50.1, lon: 8.7, hub: true },
  { name: "UAE", city: "Dubai", lat: 25.2, lon: 55.3, hub: true },
  { name: "India", city: "Bengaluru", lat: 12.97, lon: 77.6, hub: true },
  { name: "Nigeria", city: "Lagos", lat: 6.5, lon: 3.4, hub: false },
  { name: "Botswana", city: "Gaborone", lat: -24.6, lon: 25.9, hub: false },
  { name: "Zambia", city: "Lusaka", lat: -15.4, lon: 28.3, hub: false },
  { name: "Zimbabwe", city: "Harare", lat: -17.8, lon: 31.0, hub: false },
  { name: "Saudi Arabia", city: "Riyadh", lat: 24.7, lon: 46.7, hub: false },
  { name: "Kenya", city: "Nairobi", lat: -1.3, lon: 36.8, hub: false },
  { name: "Oman", city: "Muscat", lat: 23.6, lon: 58.5, hub: false },
];

// Dot-matrix continent silhouettes — hand-tuned polygon points to keep the file
// tiny while still giving a recognisable world outline.
const CONTINENT_PATHS = [
  // North America
  "M170,120 L215,105 L245,120 L270,150 L245,190 L215,215 L180,220 L155,195 L145,155 Z",
  // Central + South America
  "M235,235 L265,225 L285,255 L295,300 L275,350 L255,385 L245,410 L235,395 L225,355 L235,290 Z",
  // Europe
  "M470,110 L525,105 L555,120 L555,145 L520,160 L490,155 L470,140 Z",
  // Africa
  "M485,175 L555,165 L595,200 L610,270 L590,325 L555,355 L525,340 L495,290 L485,235 Z",
  // Middle East
  "M580,155 L640,145 L670,175 L660,205 L620,205 L595,190 Z",
  // Russia / Central Asia
  "M540,80 L740,75 L820,95 L820,140 L740,150 L640,135 L560,125 Z",
  // South Asia (India)
  "M695,180 L740,175 L750,230 L720,255 L705,225 Z",
  // East Asia / SE Asia
  "M770,150 L830,155 L860,190 L855,230 L810,245 L780,220 L775,190 Z",
  // Australia
  "M830,325 L890,320 L910,360 L890,385 L840,380 L820,350 Z",
];

export default function WorldMap() {
  const ref = useReveal();
  const [active, setActive] = useState(null);
  return (
    <section
      ref={ref}
      data-testid="world-map"
      className="reveal section border-b border-[color:var(--line)] bg-[color:var(--paper-alt)] relative overflow-hidden"
    >
      <div className="container-x relative">
        <div className="grid lg:grid-cols-12 gap-8 mb-8 md:mb-10">
          <div className="lg:col-span-5">
            <div className="flex items-center gap-3">
              <span className="w-6 h-px bg-[color:var(--brand)]" />
              <div className="eyebrow">Where we ship</div>
            </div>
            <h2 className="font-display font-black text-4xl md:text-5xl mt-4 leading-[1.02] tracking-tight">
              Eleven countries, one accountable team.
            </h2>
            <p className="mt-5 text-neutral-700 leading-relaxed max-w-lg">
              Delivery hubs in Dubai, Bengaluru, Frankfurt and London — with active rollouts across
              Africa, GCC and Europe. Hover a pin to see the local city.
            </p>
            <div className="mt-6 flex flex-wrap gap-3 text-xs font-mono uppercase tracking-widest">
              <span className="flex items-center gap-2"><span className="w-3 h-3 bg-[color:var(--brand)]" /> Delivery hub</span>
              <span className="flex items-center gap-2"><span className="w-3 h-3 border-2 border-[color:var(--brand)] bg-white" /> Live client</span>
            </div>
          </div>

          <div className="lg:col-span-7">
            <div className="relative bg-white border border-[color:var(--line)] p-4 md:p-6">
              <svg viewBox="0 0 1000 500" className="w-full h-auto block" aria-label="Countries where Maxine is live">
                {/* Dotted grid background */}
                <defs>
                  <pattern id="dots" x="0" y="0" width="14" height="14" patternUnits="userSpaceOnUse">
                    <circle cx="1" cy="1" r="1" fill="#e5e5e5" />
                  </pattern>
                </defs>
                <rect x="0" y="0" width="1000" height="500" fill="url(#dots)" />

                {/* Continent silhouettes */}
                {CONTINENT_PATHS.map((d, i) => (
                  <path key={i} d={d} fill="#dcdcdc" stroke="#c9c9c9" strokeWidth="1" />
                ))}

                {/* Connection lines from Dubai hub to all clients */}
                {(() => {
                  const dubai = project(25.2, 55.3);
                  return COUNTRIES.filter(c => !c.hub).map((c) => {
                    const p = project(c.lat, c.lon);
                    return (
                      <line
                        key={`l-${c.name}`}
                        x1={dubai.x} y1={dubai.y} x2={p.x} y2={p.y}
                        stroke="#1E88F0"
                        strokeOpacity="0.35"
                        strokeWidth="1"
                        strokeDasharray="3 3"
                      />
                    );
                  });
                })()}

                {/* Pins */}
                {COUNTRIES.map((c) => {
                  const p = project(c.lat, c.lon);
                  const isActive = active === c.name;
                  return (
                    <g
                      key={c.name}
                      data-testid={`map-pin-${c.name.toLowerCase().replace(/\s+/g, "-")}`}
                      onMouseEnter={() => setActive(c.name)}
                      onMouseLeave={() => setActive(null)}
                      style={{ cursor: "pointer" }}
                    >
                      {c.hub ? (
                        <rect x={p.x - 7} y={p.y - 7} width="14" height="14" fill="#1E88F0" stroke="#0b3f78" strokeWidth="1.5" />
                      ) : (
                        <circle cx={p.x} cy={p.y} r="7" fill="#fff" stroke="#1E88F0" strokeWidth="2.5" />
                      )}
                      {isActive && (
                        <circle cx={p.x} cy={p.y} r="14" fill="none" stroke="#1E88F0" strokeWidth="1.5" opacity="0.6">
                          <animate attributeName="r" from="8" to="20" dur="1.2s" repeatCount="indefinite" />
                          <animate attributeName="opacity" from="0.7" to="0" dur="1.2s" repeatCount="indefinite" />
                        </circle>
                      )}
                      <text
                        x={p.x + (p.x > 500 ? 12 : -12)}
                        y={p.y - 10}
                        textAnchor={p.x > 500 ? "start" : "end"}
                        fontFamily="'IBM Plex Mono', monospace"
                        fontSize="11"
                        fontWeight={isActive ? 700 : 500}
                        fill={isActive ? "#050505" : "#525252"}
                      >
                        {c.name}
                      </text>
                    </g>
                  );
                })}
              </svg>
              <div className="mt-3 min-h-[24px] font-mono text-xs text-neutral-600">
                {active ? (
                  <span>
                    <span className="text-[color:var(--brand)] font-bold">{active}</span>
                    {" · "}
                    {COUNTRIES.find(c => c.name === active)?.city}
                  </span>
                ) : (
                  <span className="text-neutral-400">Hover a pin to see the city</span>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
